import classNames from 'classnames';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import Marquee from 'react-fast-marquee';
import Lightbox from 'react-spring-lightbox';
import Button from '~/components/Button';
import Container from '~/components/Container';
import Icon from '~/components/Icon';

import { useRouter } from 'next/router';
import ShiftingCountDown from '~/components/SneakerRelease/CountDown';
import { PATH } from '~/constants/enum';
import { formatDate } from '~/services/sneaker-release/sneaker-release-transform';
import { SneakLaunch } from '~/services/sneaker-release/sneaker-release.type';
import { formatNumber } from '~/utils/number';
import OtherRelease from '../OtherRelease/OtherRelease';
import RelatedProduct from '../RelatedProduct';
import styles from './Detail.module.scss';
import Link from 'next/link';
import { getContrastingTextColor } from '~/utils/helper';

type DetailLaunchProps = {
    product?: SneakLaunch;
};

const DetailLaunch = ({ product }: DetailLaunchProps) => {
    const [openLightBox, setOpenLightBox] = useState(false);
    const [currentLightBoxIndex, setCurrentLightBoxIndex] = useState(0);
    const query = useRouter().query;

    const router = useRouter();

    const {
        releaseDate,
        title,
        color,
        thumbnail,
        htmlContent,
        styleCode,
        images = [],
        brand,
        pageTheme,
        retail,
        model,
        publishDate
    } = product || {};

    const information = useMemo(() => {
        return [
            {
                title: 'Release',
                value: (
                    <ShiftingCountDown
                        from={product?.releaseDate}
                        style={{ color: getContrastingTextColor(pageTheme || '') }}
                    />
                )
            },
            {
                title: 'Brand',
                value: brand ?? '--'
            },
            {
                title: 'Model',
                value: model?.title ?? '--'
            },
            {
                title: 'Color',
                value: color ?? '--'
            },
            {
                title: 'Price',
                value: !!Number(retail) ? `$${formatNumber(Number(retail), 2)}` : 'Updating'
            }
        ];
    }, [product]);
    const hrefFindMatch = useMemo(() => {
        if (product?.tags) {
            return PATH.CollectionDetail.replaceAll('[slug]', product?.tags);
        } else {
            return PATH.Search + '?keyword=' + title;
        }
    }, [product?.tags, title]);

    return (
        <div className="relative z-0 h-full pb-4">
            <div style={{ backgroundColor: pageTheme }}>
                <div>
                    <div className="w-full col-span-full flex md:flex-grow md:min-h-[20vw] items-center justify-center">
                        <picture className="mt-16 mb-7 md:my-28">
                            <img
                                src={thumbnail?.root}
                                loading="eager"
                                className="max-h-[384px] w-auto object-contain"
                                style={{ objectPosition: '50% 50%' }}
                            />
                        </picture>
                    </div>
                    <div
                        className="flex col-span-full px-2 lg:px-4 flex-col justify-end gap-4
                                     md:flex-row-reverse md:items-center "
                        style={{
                            color: getContrastingTextColor(pageTheme || '')
                        }}
                    >
                        <h1 className="z-1 text-[2.5rem] md:text-[4rem] font-normal tracking-[-0.02em] md:mb-12 mb-6 max-w-3xl  lg:max-w-none self-center">
                            {title} Release Date On {formatDate(releaseDate, 'MMMM D, YYYY')}
                        </h1>
                        <div className="font-mono text-sm uppercase md:w-1/2">
                            {information.map((info, index) => {
                                return (
                                    <div className="mb-0" key={index}>
                                        <div className="flex items-center">
                                            <p className="opacity-50 w-1/2 max-w-[10rem] flex-shrink-0 flex-grow-0">
                                                {info.title}
                                            </p>
                                            <p className="mb-2 w-52">
                                                <span
                                                    style={{
                                                        color: getContrastingTextColor(pageTheme || '')
                                                    }}
                                                >
                                                    {info.value}
                                                </span>
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <Marquee>
                        <div className="absolute h-[1px] top-0 left-0 right-0 bg-current opacity-50"></div>
                        <div className="absolute h-[1px] bottom-0 left-0 right-0 bg-current opacity-50"></div>
                        <div className="select-none flex flex-nowrap">
                            <div
                                className="flex justify-center items-center relative shrink-0 text-black my-1"
                                aria-hidden="true"
                                style={{
                                    color: getContrastingTextColor(pageTheme || '')
                                }}
                            >
                                {Array.from({ length: 18 }).map((_, index) => (
                                    <span className={classNames(styles.marqueeItem, 'text-sm mr-10')} key={index}>
                                        <span>{formatDate(releaseDate)}</span>
                                    </span>
                                ))}
                            </div>
                        </div>
                    </Marquee>
                </div>
            </div>
            <Container className="!max-w-[1440px] lg:!px-0">
                <div className="flex flex-col gap-10 mt-5 md:mt-10">
                    <div>
                        <div className="grid md:grid-cols-12 grid-cols-1 gap-8 relative place-content-end">
                            <Lightbox
                                isOpen={openLightBox}
                                currentIndex={currentLightBoxIndex}
                                images={images?.map((img) => ({
                                    src: img?.path,
                                    loading: 'lazy',
                                    alt: 'image'
                                }))}
                                onNext={() => setCurrentLightBoxIndex(currentLightBoxIndex + 1)}
                                onPrev={() => setCurrentLightBoxIndex(currentLightBoxIndex - 1)}
                                renderHeader={() => (
                                    <div className="lightbox-image-pager flex justify-end p-4">
                                        <div
                                            className="w-10 h-10 cursor-pointer flex items-center justify-center"
                                            onClick={() => setOpenLightBox(false)}
                                        >
                                            <Icon name="close" color="white" size={32} />
                                        </div>
                                    </div>
                                )}
                                renderFooter={() => (
                                    <div
                                        className={classNames(
                                            styles.lighbox_list,
                                            'flex justify-center gap-3 py-6 lightbox-image-pager'
                                        )}
                                    >
                                        {images.map((img, index) => (
                                            <div
                                                key={index}
                                                className={classNames(styles.lightbox_image, 'cursor-pointer', {
                                                    [styles.selected]: currentLightBoxIndex === index
                                                })}
                                                onClick={() => {
                                                    setCurrentLightBoxIndex(index);
                                                }}
                                            >
                                                <img src={img?.path} alt={img?.path} width={80} height={80} />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            />
                            <div
                                className={classNames(
                                    'text-center col-span-12 md:col-span-4 flex flex-col gap-6 md:sticky top-20'
                                )}
                                style={{ alignSelf: 'start' }}
                            >
                                <div className="text-center md:text-start">
                                    <h2 className="font-bold text-4xl my-2">{title}</h2>
                                    {/* <p className="font-semibold text-lg">
                                        {!!Number(retail) ? `$${formatNumber(Number(retail), 2)}` : 'Updating'}
                                    </p> */}
                                    {releaseDate && (
                                        <p>
                                            Available {formatDate(releaseDate, 'MM/DD')} at{' '}
                                            {formatDate(releaseDate, 'HH:mm A')}
                                        </p>
                                    )}
                                    <div
                                        className="mt-4 text-justify"
                                        dangerouslySetInnerHTML={{ __html: htmlContent ?? '' }}
                                    />
                                    {styleCode && <div className="font-semibold text-base my-6">SKU: {styleCode}</div>}
                                </div>
                                <div className="grid grid-cols-12 gap-2">
                                    <div className="col-span-12 flex justify-center">
                                        <Link href={hrefFindMatch} target="_blank">
                                            <Button type="solid" className="w-full">
                                                Find Shirts To Match Sneaker
                                            </Button>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                            <div className="md:col-span-8 col-span-12">
                                <div className="grid grid-cols-12 gap-4 justify-end">
                                    {images.map((image, index) => {
                                        return (
                                            <div key={index} className="w-full col-span-12 md:col-span-6">
                                                <img
                                                    src={image?.path}
                                                    onClick={() => {
                                                        setOpenLightBox(true);
                                                        setCurrentLightBoxIndex(index);
                                                    }}
                                                    className="cursor-pointer w-full object-contain"
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div>
                        <div className="flex flex-col gap-5">
                            <h2 className="font-bold text-4xl" style={{ lineHeight: 'unset' }}>
                                How To Style Your Nike {title}?
                            </h2>
                            <p>
                                It’s important to create a balanced look when styling your Air Jordan {model?.title}{' '}
                                sneakers. Let start with tapered jeans or cargo pants that allow the sneakers to stand
                                out. To match your top with the shoes, go for a neutral-colored shirt or one with subtle
                                accents that complement the sneaker’s design. Pairing the outfit with crew socks in
                                black or white will keep things clean and cohesive. For an enhanced look, consider
                                matching your outfit with Dunkare’s shirt & sock made to match {title}, which are
                                specifically designed to pair with the sneaker's color, ensuring a stylish and
                                streetvibe.
                            </p>
                            <p>
                                When you need the perfect shirt to match your sneakers, head to Dunkare. Dunkare offers
                                apparel with unique shirts, hoodies, socks, and more that matches {title}. Our
                                collection is designed to enhance the colorways and design elements of {title}
                                like these, offering a cohesive style from head to toe.
                            </p>
                            <div>
                                <RelatedProduct keyword={title} product={product} />
                            </div>
                        </div>
                    </div>
                    <OtherRelease brandCode={product?.brandCode as string} />
                </div>
            </Container>
        </div>
    );
};

export default DetailLaunch;
